import { Component } from '@theme/component';
import { debounce, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';

class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults'];
  #controller = new AbortController();
  #activeFetch = null;
  #emptyStateLoaded = false;
  #isKeyboardNavigation = false;

  get nativeDialog() {
    return /** @type {HTMLDialogElement | null} */ (this.closest('dialog-component')?.querySelector(':scope > dialog'));
  }

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#controller;

    document.addEventListener('keydown', this.#handleDocumentKeyDown, { signal });
    document.addEventListener('click', this.#handleDocumentClick, { signal, capture: true });
    this.addEventListener('click', this.#handleSearchContentClick, { signal });
    this.querySelector('form')?.addEventListener('submit', this.#handleSubmit, { signal });

    requestIdleCallback(() => this.#loadEmptyState());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  #isMobile() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  #handleDocumentClick = async (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const openButton = target.closest('[data-parel-search-open]');

    if (openButton) {
      event.preventDefault();
      event.stopImmediatePropagation();

      await this.#closeMenuBeforeSearch();

      if (openButton instanceof HTMLElement) {
        openButton.blur();
      }

      this.#openSearch();
      return;
    }

    const closeButton = target.closest('[data-parel-search-close]');
    if (closeButton && this.contains(closeButton)) {
      event.preventDefault();
      this.#closeSearch();
      return;
    }

    const dialog = this.nativeDialog;
    if (!dialog?.open) return;

    if (this.#isMobile()) {
      const clickedHeaderAction = target.closest('header, header-component, .header');
      const clickedInsideSearch = target.closest('#search-modal');

      if (clickedHeaderAction && !clickedInsideSearch) {
        this.#closeSearch(false);
      }
    } else if (target === dialog) {
      this.#closeSearch();
    }
  };

  async #closeMenuBeforeSearch() {
    const headerDrawer = document.querySelector('header-drawer');
    const details = document.getElementById('Details-menu-drawer-container');

    if (!details?.open) return;

    /*
     * Close through Horizon's public HeaderDrawer.close() method.
     * This immediately removes .menu-open (so X becomes burger again),
     * then Horizon removes the details[open] state after the drawer
     * transition and releases its focus/scroll lock cleanly.
     */
    if (headerDrawer && typeof headerDrawer.close === 'function') {
      headerDrawer.close();
    } else {
      details.classList.remove('menu-open');
      details.removeAttribute('open');
      details.querySelector(':scope > summary')?.setAttribute('aria-expanded', 'false');
    }

    const summary = details.querySelector(':scope > summary');
    if (summary instanceof HTMLElement) {
      summary.blur();
    }

    if (!details.open) {
      await this.#releaseClosedMenuScrollLock();
      return;
    }

    await new Promise((resolve) => {
      let settled = false;

      const finish = () => {
        if (settled) return;
        settled = true;
        details.removeEventListener('toggle', onToggle);
        clearTimeout(fallback);
        resolve();
      };

      const onToggle = () => {
        if (!details.open) finish();
      };

      details.addEventListener('toggle', onToggle);

      const fallback = window.setTimeout(() => {
        /*
         * Safety only: if a transition event is suppressed, do not let
         * Search open on top of a stale menu state.
         */
        if (details.open) {
          details.classList.remove('menu-open');
          details.removeAttribute('open');
          details.querySelector(':scope > summary')?.setAttribute('aria-expanded', 'false');
        }
        finish();
      }, 450);
    });

    await this.#releaseClosedMenuScrollLock();
  }

  #lockSearchPage() {
    document.documentElement.classList.add('parel-search-page-locked');
    document.body.classList.add('parel-search-page-locked');
  }

  #unlockSearchPage() {
    document.documentElement.classList.remove('parel-search-page-locked');
    document.body.classList.remove('parel-search-page-locked');
  }

  async #releaseClosedMenuScrollLock() {
    const details = document.getElementById('Details-menu-drawer-container');

    if (details?.open || details?.classList.contains('menu-open')) {
      return;
    }

    /*
     * Horizon's <details scroll-lock> owns the `scroll-lock` attribute on
     * the root document while Menu is open. On rapid Menu/Search handoffs
     * iOS can keep that lock one turn too long. Once Menu is fully closed,
     * Search becomes the only scroll-lock owner, so clear the stale native
     * menu lock before Search opens.
     */
    document.documentElement.removeAttribute('scroll-lock');
    document.body.removeAttribute('scroll-lock');

    await new Promise((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(resolve));
    });
  }

  #handleDocumentKeyDown = (event) => {
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      if (this.nativeDialog?.open) this.#closeSearch();
      else this.#openSearch();
      return;
    }

    if (event.key === 'Escape' && this.nativeDialog?.open) {
      event.preventDefault();
      this.#closeSearch();
    }
  };

  #openSearch() {
    const dialog = this.nativeDialog;
    if (!dialog || dialog.open) return;

    this.#lockSearchPage();

    if (this.#isMobile()) {
      dialog.show();
    } else {
      dialog.showModal();
    }

    dialog.classList.remove('parel-search-closing');

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        dialog.classList.add('parel-search-visible');
      });

      this.refs.searchInput.focus();
      this.#loadEmptyState();
    });
  }

  #closeSearch(reset = true) {
    const dialog = this.nativeDialog;
    if (!dialog?.open) return;

    /*
     * Mobile Search has no closing animation. Close the native dialog
     * immediately so its scroll-lock is fully released before another
     * header drawer (Menu/Cart) is allowed to open.
     *
     * This prevents the sequence Search -> Menu -> Search from leaving
     * competing scroll-lock states behind.
     */
    if (this.#isMobile()) {
      /*
       * Release Search's lock BEFORE Menu/Cart receives the same header tap.
       * This makes the lock handoff deterministic on iOS.
       */
      this.#unlockSearchPage();

      dialog.classList.remove('parel-search-visible', 'parel-search-closing');

      if (dialog.open) {
        dialog.close();
      }

      if (reset) this.#resetSearch();
      return;
    }

    dialog.classList.remove('parel-search-visible');
    dialog.classList.add('parel-search-closing');

    window.setTimeout(() => {
      dialog.classList.remove('parel-search-closing');
      if (dialog.open) dialog.close();
      this.#unlockSearchPage();
    }, 300);

    if (reset) this.#resetSearch();
  }

  #handleSubmit = (event) => {
    event.preventDefault();

    if (this.#isMobile()) {
      this.refs.searchInput.blur();
    }
  };

  #handleSearchContentClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const popularLink = target.closest('[data-parel-search-term]');

    if (popularLink instanceof HTMLElement) {
      event.preventDefault();
      const term = popularLink.dataset.parelSearchTerm || '';
      this.refs.searchInput.value = term;
      this.#getSearchResults(term);
      this.refs.searchInput.focus();
      return;
    }

    if (!target.closest('button, a, input')) this.refs.searchInput.focus();
  };

  async #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    await this.#renderEmptyState(false);
  }

  get #allResultsItems() {
    return /** @type {HTMLElement[]} */ (
      Array.from(this.querySelectorAll('[ref="resultsItems[]"]')).filter((item) => item instanceof HTMLElement)
    );
  }

  get #currentIndex() {
    return this.#allResultsItems.findIndex((item) => item.getAttribute('aria-selected') === 'true');
  }

  set #currentIndex(index) {
    const items = this.#allResultsItems;
    if (!items.length) return;

    items.forEach((item, i) => {
      item.classList.toggle('keyboard-focus', i === index && this.#isKeyboardNavigation);
      if (i === index) item.setAttribute('aria-selected', 'true');
      else item.removeAttribute('aria-selected');
    });

    items[index]?.scrollIntoView({
      behavior: prefersReducedMotion() ? 'instant' : 'smooth',
      block: 'nearest',
    });

    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems[this.#currentIndex];
  }

  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.#closeSearch();
      return;
    }

    const items = this.#allResultsItems;
    const currentIndex = this.#currentIndex;

    if (event.key === 'Enter') {
      // The keyboard Search/Enter key must stay inside the drawer.
      // Only VIEW ALL / ALL RESULTS may navigate to the full search page.
      event.preventDefault();

      if (currentIndex >= 0) {
        this.#currentItem?.querySelector('a')?.click();
        return;
      }

      if (this.#isMobile()) {
        this.refs.searchInput.blur();
      }

      return;
    }

    if (!items.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') return;

    if (event.key === 'ArrowDown' || (event.key === 'Tab' && !event.shiftKey)) {
      this.#isKeyboardNavigation = true;
      event.preventDefault();
      this.#currentIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0;
    } else if (event.key === 'ArrowUp' || (event.key === 'Tab' && event.shiftKey)) {
      this.#isKeyboardNavigation = true;
      event.preventDefault();
      this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1;
    }
  };

  resetSearch = debounce((keepFocus = true) => {
    this.#resetSearch();
    if (keepFocus) this.refs.searchInput.focus();
  }, 100);

  search = debounce((event) => {
    if (!event.inputType) return;

    const term = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!term) {
      this.#resetSearch();
      return;
    }

    this.#getSearchResults(term);
  }, 180);

  #resetScrollPositions() {
    requestAnimationFrame(() => this.refs.predictiveSearchResults.scrollTo(0, 0));
  }

  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const predictiveUrl = new URL(Theme.routes.predictive_search_url, location.origin);
    predictiveUrl.searchParams.set('q', searchTerm);
    predictiveUrl.searchParams.set('resources[type]', 'product');
    predictiveUrl.searchParams.set('resources[limit]', '8');
    predictiveUrl.searchParams.set('resources[limit_scope]', 'each');

    const countUrl = new URL(Theme.routes.search_url, location.origin);
    countUrl.searchParams.set('q', searchTerm);
    countUrl.searchParams.set('type', 'product');
    countUrl.searchParams.set('options[prefix]', 'last');

    const abortController = this.#createAbortController();

    try {
      const [resultsMarkup, countMarkup] = await Promise.all([
        sectionRenderer.getSectionHTML(this.dataset.sectionId, false, predictiveUrl),
        sectionRenderer.getSectionHTML(this.dataset.sectionId, false, countUrl),
      ]);

      if (!resultsMarkup || abortController.signal.aborted) return;

      morph(this.refs.predictiveSearchResults, resultsMarkup);

      if (countMarkup) {
        const countDocument = new DOMParser().parseFromString(countMarkup, 'text/html');
        const countSource = countDocument.querySelector('[data-parel-visible-count]');
        const countTarget = this.refs.predictiveSearchResults.querySelector('[data-parel-total-count]');

        if (countSource instanceof HTMLElement && countTarget instanceof HTMLElement) {
          const visibleCount = countSource.dataset.parelVisibleCount;
          if (visibleCount != null && visibleCount !== '') {
            countTarget.textContent = visibleCount;
          }
        }
      }

      this.#resetScrollPositions();
    } catch (error) {
      if (!abortController.signal.aborted) console.error(error);
    }
  }


  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (!viewedProducts.length) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.slice(0, 6).map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #createAbortController() {
    if (this.#activeFetch) this.#activeFetch.abort();
    this.#activeFetch = new AbortController();
    return this.#activeFetch;
  }

  #resetSearch = async () => {
    this.refs.searchInput.value = '';
    this.#currentIndex = -1;
    await this.#renderEmptyState(true);
  };

  async #renderEmptyState(resetScroll = true) {
    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptyMarkup = await sectionRenderer.getSectionHTML('predictive-search-empty', false, url);
    if (abortController.signal.aborted || !emptyMarkup) return;

    const parsed = new DOMParser().parseFromString(emptyMarkup, 'text/html');
    const emptySection = parsed.querySelector('.predictive-search-empty-section');
    if (!emptySection) return;

    const slot = emptySection.querySelector('#parel-recently-viewed-slot');
    const viewedProducts = RecentlyViewed.getProducts();

    if (slot && viewedProducts.length > 0) {
      const recentMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (abortController.signal.aborted) return;

      if (recentMarkup) {
        const recentDoc = new DOMParser().parseFromString(recentMarkup, 'text/html');
        const recentProducts = recentDoc.querySelector('.parel-search-products--recent');

        if (recentProducts) {
          const section = document.createElement('section');
          section.className = 'parel-search-merch-section parel-search-merch-section--recently-viewed';
          section.append(recentProducts);
          slot.append(section);
        }
      }
    }

    if (abortController.signal.aborted) return;

    morph(this.refs.predictiveSearchResults, emptySection);
    if (resetScroll) this.#resetScrollPositions();
  }
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
